"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Phone,
  Video,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type AppointmentType = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_options: number[];
  buffer_minutes: number;
};

type SlotsResponse = {
  availableDates: string[];
  slotsByDate: Record<string, string[]>;
};

// Themen-Vorauswahl im Schritt „Worum geht's?" – die Namen müssen den
// öffentlichen Service-Bezeichnungen aus dem Header-Dropdown entsprechen
// (siehe components/Header.tsx).
const TOPIC_OPTIONS = [
  "Website-Entwicklung",
  "Suchmaschinen-Optimierung",
  "Social Media Marketing",
  "Markenidentität",
  "Software & App-Entwicklung",
  "Online-Werbung",
  "Wartung & Hosting",
  "Domain-Verwaltung",
  "Allgemein / Beratung",
];

export function BookingFlow({ types }: { types: AppointmentType[] }) {
  // Wir vereinfachen den Flow: weder Termin-Art noch Dauer werden vom
  // Kunden gewählt. Default-Typ = erster aktiver (Reihenfolge "Erstgespräch"
  // zuerst, siehe DB-sort_order), Default-Dauer = maximaler Wert aus den
  // duration_options (i. d. R. 60 Min, damit wir genug Puffer blocken; der
  // Termin kann je nach Bedarf auch nach 30 Min beendet werden).
  const defaultType = types[0] ?? null;
  const defaultDuration = defaultType
    ? Math.max(...defaultType.duration_options)
    : 60;
  const minDuration = defaultType
    ? Math.min(...defaultType.duration_options)
    : 30;

  const [typeId] = useState<string>(defaultType?.id ?? "");
  const [duration] = useState<number>(defaultDuration);
  const selectedType = defaultType;
  // Slots
  const [slots, setSlots] = useState<SlotsResponse | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  // Daten
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState<"google_meet" | "phone">(
    "google_meet",
  );
  const [topics, setTopics] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Nur noch zwei Schritte: 1 = Zeit, 2 = Daten.
  const [step, setStep] = useState<1 | 2>(1);

  // Slots werden direkt beim Mount geladen – kein vorgelagerter Klick mehr.
  useEffect(() => {
    if (!typeId || !duration) return;
    let active = true;
    setSlotsLoading(true);
    setSlotsError(null);
    fetch(`/api/booking/slots?typeId=${typeId}&duration=${duration}`)
      .then(async (r) => {
        // Robust: Body als Text lesen und nur parsen, wenn vorhanden –
        // sonst crasht r.json() bei leerer/Nicht-JSON-Antwort mit
        // „Unexpected end of JSON input".
        const text = await r.text();
        let json: SlotsResponse | { error?: string } = {};
        try {
          json = text ? JSON.parse(text) : {};
        } catch {
          json = {};
        }
        if (!r.ok) {
          throw new Error(
            ("error" in json && json.error) ||
              "Die verfügbaren Zeiten konnten gerade nicht geladen werden. Bitte später erneut versuchen.",
          );
        }
        return json as SlotsResponse;
      })
      .then((data) => {
        if (!active) return;
        setSlots(data);
        if (data.availableDates.length > 0 && !selectedDate) {
          setSelectedDate(data.availableDates[0]);
        }
      })
      .catch((e) => active && setSlotsError((e as Error).message))
      .finally(() => active && setSlotsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, duration]);

  async function submit() {
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          typeId,
          duration,
          startAt: selectedSlot,
          name,
          email,
          phone,
          channel,
          topics,
          message,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Buchung fehlgeschlagen");
      setDone(true);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done && selectedSlot) {
    const at = new Date(selectedSlot);
    return (
      <div
        className="rounded-2xl border p-8 text-center"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--card-bg)",
        }}
      >
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
          style={{
            backgroundColor: "var(--fi-success-bg)",
            color: "var(--fi-success-fg)",
          }}
        >
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2
          className="mt-4 text-2xl font-semibold"
          style={{ color: "var(--text)" }}
        >
          Termin gebucht!
        </h2>
        <p className="mt-3 text-base" style={{ color: "var(--text-secondary)" }}>
          Wir haben Ihnen eine Bestätigungs-Mail an{" "}
          <strong style={{ color: "var(--text)" }}>{email}</strong> geschickt.
        </p>
        <div
          className="mx-auto mt-6 max-w-sm rounded-xl border p-4 text-left"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--bg-secondary)",
          }}
        >
          <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-quaternary)" }}>
            Ihr Termin
          </p>
          <p
            className="mt-1 text-sm font-semibold"
            style={{ color: "var(--text)" }}
          >
            {selectedType?.name ?? "Termin"} · {minDuration}–{defaultDuration} Min
          </p>
          <p className="mt-0.5 text-sm" style={{ color: "var(--text-secondary)" }}>
            {at.toLocaleDateString("de-DE", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              year: "numeric",
              timeZone: "Europe/Berlin",
            })}
          </p>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {at.toLocaleTimeString("de-DE", {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Europe/Berlin",
            })}{" "}
            Uhr
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
      }}
    >
      <Stepper step={step} />

      <div className="p-6 lg:p-8">
        {step === 1 && (
          <Step3Slots
            slots={slots}
            loading={slotsLoading}
            error={slotsError}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            durationHint={`${minDuration}–${defaultDuration} Min`}
            onPickDate={setSelectedDate}
            onPickSlot={setSelectedSlot}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && selectedSlot && (
          <Step4Details
            slotIso={selectedSlot}
            durationLabel={`${minDuration}–${defaultDuration} Min`}
            typeName={selectedType?.name ?? "Termin"}
            name={name}
            email={email}
            phone={phone}
            channel={channel}
            topics={topics}
            message={message}
            consent={consent}
            onChange={(p) => {
              if (p.name !== undefined) setName(p.name);
              if (p.email !== undefined) setEmail(p.email);
              if (p.phone !== undefined) setPhone(p.phone);
              if (p.channel !== undefined) setChannel(p.channel);
              if (p.topics !== undefined) setTopics(p.topics);
              if (p.message !== undefined) setMessage(p.message);
              if (p.consent !== undefined) setConsent(p.consent);
            }}
            onBack={() => setStep(1)}
            onSubmit={submit}
            submitting={submitting}
            error={submitError}
          />
        )}
      </div>
    </div>
  );
}

/* ────────────────────────  Stepper  ──────────────────────── */

function Stepper({ step }: { step: 1 | 2 }) {
  const labels = ["Zeit wählen", "Ihre Daten"];
  return (
    <div
      className="flex items-center justify-between border-b px-6 py-3"
      style={{ borderColor: "var(--border)" }}
    >
      {labels.map((label, i) => {
        const stepNum = (i + 1) as 1 | 2;
        const active = step === stepNum;
        const done = step > stepNum;
        return (
          <div key={label} className="flex flex-1 items-center gap-2">
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold"
              style={{
                backgroundColor: done
                  ? "var(--bg-success-secondary)"
                  : active
                    ? "var(--fi-brand-bg)"
                    : "var(--bg-secondary)",
                color: done
                  ? "var(--text-success-primary)"
                  : active
                    ? "var(--fi-brand-fg)"
                    : "var(--text-tertiary)",
              }}
            >
              {done ? "✓" : i + 1}
            </span>
            <span
              className="hidden text-xs font-medium sm:inline"
              style={{
                color: active
                  ? "var(--text)"
                  : done
                    ? "var(--text-secondary)"
                    : "var(--text-tertiary)",
              }}
            >
              {label}
            </span>
            {i < labels.length - 1 && (
              <span
                className="h-px flex-1 sm:ml-2"
                style={{
                  backgroundColor: done
                    ? "var(--text-success-primary)"
                    : "var(--border)",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────  Step 1: Zeit wählen  ───────────────── */

function Step3Slots({
  slots,
  loading,
  error,
  selectedDate,
  selectedSlot,
  durationHint,
  onPickDate,
  onPickSlot,
  onNext,
}: {
  slots: SlotsResponse | null;
  loading: boolean;
  error: string | null;
  selectedDate: string | null;
  selectedSlot: string | null;
  durationHint: string;
  onPickDate: (date: string) => void;
  onPickSlot: (iso: string) => void;
  onNext: () => void;
}) {
  // Welcher Monat wird gerade angezeigt? Default = Monat des ersten
  // verfügbaren Tages (oder aktueller Monat).
  const today = useMemo(() => new Date(), []);
  const initialMonth = useMemo(() => {
    const first = slots?.availableDates[0];
    if (first) {
      const d = new Date(first + "T12:00:00");
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, [slots, today]);
  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);

  // Wenn slots erstmal geladen sind und kein Datum gewählt ist:
  // erstes verfügbares vorwählen
  useEffect(() => {
    if (slots && slots.availableDates.length > 0 && !selectedDate) {
      onPickDate(slots.availableDates[0]);
      const first = new Date(slots.availableDates[0] + "T12:00:00");
      setViewMonth(new Date(first.getFullYear(), first.getMonth(), 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  return (
    <div>
      <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>
        Wann passt es Ihnen?
      </h2>
      <p className="mt-1 text-sm" style={{ color: "var(--text-tertiary)" }}>
        Kostenloses Erstgespräch · Dauer {durationHint} (wir passen uns an{" "}
        Ihr Anliegen an) · Mo–Do bis 17 Uhr, Fr bis 16 Uhr (Europe/Berlin).
      </p>

      {loading && (
        <p
          className="mt-6 flex items-center gap-2 text-sm"
          style={{ color: "var(--text-tertiary)" }}
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Lade verfügbare Zeiten …
        </p>
      )}

      {error && (
        <p
          className="mt-4 text-sm"
          style={{ color: "var(--text-error-primary)" }}
        >
          Fehler: {error}
        </p>
      )}

      {!loading && slots && slots.availableDates.length === 0 && (
        <p
          className="mt-4 text-sm"
          style={{ color: "var(--text-tertiary)" }}
        >
          In den nächsten 30 Tagen sind alle Termine ausgebucht. Schreiben Sie uns
          bitte direkt an kontakt@dmd-studio.de.
        </p>
      )}

      {!loading && slots && slots.availableDates.length > 0 && (
        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]">
          {/* Kalender */}
          <CalendarPicker
            viewMonth={viewMonth}
            onChangeMonth={setViewMonth}
            availableDates={new Set(slots.availableDates)}
            selectedDate={selectedDate}
            onPickDate={(d) => {
              onPickDate(d);
              // Slot deselecten, da Datum gewechselt
              if (selectedDate !== d) onPickSlot("");
            }}
          />

          {/* Slots-Spalte */}
          <div>
            {selectedDate ? (
              <SlotColumn
                date={selectedDate}
                slots={slots.slotsByDate[selectedDate] ?? []}
                selectedSlot={selectedSlot}
                onPickSlot={onPickSlot}
              />
            ) : (
              <div
                className="flex h-full items-center justify-center rounded-lg border p-6 text-center text-xs"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text-tertiary)",
                  minHeight: 200,
                }}
              >
                Wählen Sie links ein Datum, dann erscheinen hier die freien
                Uhrzeiten.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <NextButton onClick={onNext} disabled={!selectedSlot} />
      </div>
    </div>
  );
}

/* ─────────────────────  Kalender-Picker  ─────────────────── */

function CalendarPicker({
  viewMonth,
  onChangeMonth,
  availableDates,
  selectedDate,
  onPickDate,
}: {
  viewMonth: Date;
  onChangeMonth: (d: Date) => void;
  availableDates: Set<string>;
  selectedDate: string | null;
  onPickDate: (d: string) => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Wochentag von Tag 1: in JS ist Sonntag=0; wir wollen Montag=0.
  const startWeekday = (firstOfMonth.getDay() + 6) % 7;

  // Cells: leere Slots am Anfang + alle Tage des Monats
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  // Navigation: nicht vor aktuellen Monat, nicht weiter als 30 Tage voraus
  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxDate = new Date(today.getTime() + 31 * 86400_000);
  const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const canPrev = viewMonth > minMonth;
  const canNext = viewMonth < maxMonth;

  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
    >
      <div className="mb-2 flex items-center justify-between px-1">
        <p
          className="text-sm font-semibold"
          style={{ color: "var(--text)" }}
        >
          {viewMonth.toLocaleDateString("de-DE", {
            month: "long",
            year: "numeric",
          })}
        </p>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => onChangeMonth(new Date(year, month - 1, 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-brand-500/10 disabled:opacity-30"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => onChangeMonth(new Date(year, month + 1, 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-brand-500/10 disabled:opacity-30"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Nächster Monat"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
          <div
            key={d}
            className="py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--text-quaternary)" }}
          >
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;
          const local = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
          const available = availableDates.has(local);
          const selected = local === selectedDate;
          const isPast = day < today;

          return (
            <button
              key={local}
              type="button"
              disabled={!available || isPast}
              onClick={() => onPickDate(local)}
              className="relative flex aspect-square items-center justify-center rounded-md text-sm font-medium transition-colors"
              style={
                selected
                  ? {
                      backgroundColor: "var(--fi-brand-fg)",
                      color: "#fff",
                    }
                  : available
                    ? {
                        backgroundColor: "var(--fi-brand-bg)",
                        color: "var(--fi-brand-fg)",
                      }
                    : {
                        color: isPast
                          ? "var(--text-quaternary)"
                          : "var(--text-tertiary)",
                        opacity: 0.45,
                      }
              }
            >
              {day.getDate()}
              {available && !selected && (
                <span
                  className="absolute bottom-1 h-1 w-1 rounded-full"
                  style={{ backgroundColor: "var(--fi-brand-fg)" }}
                />
              )}
            </button>
          );
        })}
      </div>

      <p
        className="mt-3 px-1 text-[11px]"
        style={{ color: "var(--text-quaternary)" }}
      >
        <span
          className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
          style={{ backgroundColor: "var(--fi-brand-bg)" }}
        />
        verfügbare Tage
      </p>
    </div>
  );
}

/* ─────────────────────  Slot-Spalte  ─────────────────── */

function SlotColumn({
  date,
  slots,
  selectedSlot,
  onPickSlot,
}: {
  date: string;
  slots: string[];
  selectedSlot: string | null;
  onPickSlot: (iso: string) => void;
}) {
  const day = new Date(date + "T12:00:00");
  return (
    <div>
      <p
        className="text-xs font-semibold uppercase tracking-wider"
        style={{ color: "var(--text-quaternary)" }}
      >
        {day.toLocaleDateString("de-DE", {
          weekday: "long",
          day: "2-digit",
          month: "long",
        })}
      </p>
      <div
        className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1"
        style={{
          scrollbarWidth: "thin",
        }}
      >
        {slots.length === 0 ? (
          <p
            className="text-xs"
            style={{ color: "var(--text-tertiary)" }}
          >
            Keine freien Slots an diesem Tag.
          </p>
        ) : (
          slots.map((iso) => {
            const active = iso === selectedSlot;
            const t = new Date(iso);
            const time = t.toLocaleTimeString("de-DE", {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Europe/Berlin",
            });
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onPickSlot(iso)}
                className="block w-full rounded-lg border py-2.5 text-sm font-semibold transition-colors"
                style={{
                  borderColor: active
                    ? "var(--fi-brand-fg)"
                    : "var(--border)",
                  backgroundColor: active
                    ? "var(--fi-brand-fg)"
                    : "var(--bg)",
                  color: active ? "#fff" : "var(--text)",
                }}
              >
                {time}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ────────────────────────  Step 4  ──────────────────────── */

function Step4Details({
  slotIso,
  durationLabel,
  typeName,
  name,
  email,
  phone,
  channel,
  topics,
  message,
  consent,
  onChange,
  onBack,
  onSubmit,
  submitting,
  error,
}: {
  slotIso: string;
  durationLabel: string;
  typeName: string;
  name: string;
  email: string;
  phone: string;
  channel: "google_meet" | "phone";
  topics: string[];
  message: string;
  consent: boolean;
  onChange: (p: Partial<{
    name: string;
    email: string;
    phone: string;
    channel: "google_meet" | "phone";
    topics: string[];
    message: string;
    consent: boolean;
  }>) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const at = new Date(slotIso);
  const valid =
    name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    message.trim().length >= 10 &&
    consent &&
    (channel === "google_meet" || phone.trim().length >= 5);

  return (
    <div className="space-y-5">
      <div
        className="rounded-xl border p-4"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--bg-secondary)",
        }}
      >
        <p
          className="text-xs uppercase tracking-wider"
          style={{ color: "var(--text-quaternary)" }}
        >
          Ihr Termin
        </p>
        <p
          className="mt-1 text-sm font-semibold"
          style={{ color: "var(--text)" }}
        >
          {typeName} · {durationLabel}
        </p>
        <p className="mt-0.5 text-sm" style={{ color: "var(--text-secondary)" }}>
          {at.toLocaleDateString("de-DE", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            timeZone: "Europe/Berlin",
          })}{" "}
          ·{" "}
          {at.toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Europe/Berlin",
          })}{" "}
          Uhr
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name *" htmlFor="bf-name">
          <input
            id="bf-name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => onChange({ name: e.target.value })}
            maxLength={120}
            className={inputCls}
            style={inputStyle}
          />
        </Field>
        <Field label="E-Mail *" htmlFor="bf-email">
          <input
            id="bf-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => onChange({ email: e.target.value })}
            maxLength={200}
            className={inputCls}
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Wie sollen wir uns treffen? *">
        <div className="grid grid-cols-2 gap-2">
          <ChannelButton
            active={channel === "google_meet"}
            onClick={() => onChange({ channel: "google_meet" })}
            icon={Video}
            label="Google Meet"
            sub="Online-Call, Link kommt zur Erinnerungs-Mail"
          />
          <ChannelButton
            active={channel === "phone"}
            onClick={() => onChange({ channel: "phone" })}
            icon={Phone}
            label="Telefon"
            sub="Wir rufen Sie zur vereinbarten Zeit an"
          />
        </div>
      </Field>

      {channel === "phone" && (
        <Field label="Telefonnummer *" htmlFor="bf-phone">
          <input
            id="bf-phone"
            type="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            maxLength={50}
            placeholder="+49 …"
            className={inputCls}
            style={inputStyle}
          />
        </Field>
      )}

      <Field label="Worum geht's?">
        <div className="flex flex-wrap gap-1.5">
          {TOPIC_OPTIONS.map((t) => {
            const active = topics.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() =>
                  onChange({
                    topics: active
                      ? topics.filter((x) => x !== t)
                      : [...topics, t],
                  })
                }
                className="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
                style={{
                  borderColor: active
                    ? "var(--fi-brand-fg)"
                    : "var(--border)",
                  backgroundColor: active
                    ? "var(--fi-brand-bg)"
                    : "var(--bg)",
                  color: active
                    ? "var(--fi-brand-fg)"
                    : "var(--text-secondary)",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Anliegen / Kontext * (mindestens 10 Zeichen)" htmlFor="bf-message">
        <textarea
          id="bf-message"
          required
          value={message}
          onChange={(e) => onChange({ message: e.target.value })}
          rows={4}
          maxLength={2000}
          placeholder="Was ist Ihr Anliegen? Je konkreter, desto besser können wir uns vorbereiten."
          className={inputCls}
          style={inputStyle}
        />
      </Field>

      <label className="flex items-start gap-2.5 text-xs" style={{ color: "var(--text-secondary)" }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => onChange({ consent: e.target.checked })}
          className="mt-0.5 h-4 w-4"
        />
        <span>
          Ich habe die{" "}
          <a
            href="/datenschutz"
            target="_blank"
            className="underline hover:text-brand-500"
          >
            Datenschutzerklärung
          </a>{" "}
          gelesen und stimme der Speicherung meiner Angaben zur
          Termin­abwicklung zu. *
        </span>
      </label>

      {error && (
        <p
          className="rounded-lg border px-3 py-2 text-sm"
          style={{
            borderColor: "var(--border-error)",
            backgroundColor: "var(--bg-error-secondary)",
            color: "var(--text-error-primary)",
          }}
        >
          {error}
        </p>
      )}

      <div className="flex justify-between">
        <BackButton onClick={onBack} disabled={submitting} />
        <button
          type="button"
          onClick={onSubmit}
          disabled={!valid || submitting}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Verbindlich buchen
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────  Helpers  ──────────────────────── */

function ChannelButton({
  active,
  onClick,
  icon: Icon,
  label,
  sub,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Video;
  label: string;
  sub: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border p-3 text-left transition-colors"
      style={{
        borderColor: active ? "var(--fi-brand-fg)" : "var(--border)",
        backgroundColor: active ? "var(--fi-brand-bg)" : "var(--bg)",
      }}
    >
      <div className="flex items-center gap-2">
        <Icon
          className="h-4 w-4"
          style={{
            color: active ? "var(--fi-brand-fg)" : "var(--text-tertiary)",
          }}
        />
        <span
          className="text-sm font-semibold"
          style={{ color: active ? "var(--fi-brand-fg)" : "var(--text)" }}
        >
          {label}
        </span>
      </div>
      <p
        className="mt-1 text-xs"
        style={{ color: "var(--text-tertiary)" }}
      >
        {sub}
      </p>
    </button>
  );
}

function BackButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:bg-brand-500/10 disabled:opacity-50"
      style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Zurück
    </button>
  );
}

function NextButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
    >
      Weiter
      <ArrowRight className="h-3.5 w-3.5" />
    </button>
  );
}

/**
 * Beschriftung + Feld. Mit `htmlFor` ist das Label programmatisch mit dem
 * Eingabefeld verknüpft (WCAG 1.3.1 / 4.1.2); für Button-Gruppen wird
 * stattdessen eine Gruppe mit sichtbarer Beschriftung gerendert.
 */
function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wider";
  if (!htmlFor) {
    const groupId = `bf-group-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
    return (
      <div role="group" aria-labelledby={groupId}>
        <span id={groupId} className={labelCls} style={{ color: "var(--text-quaternary)" }}>
          {label}
        </span>
        {children}
      </div>
    );
  }
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls} style={{ color: "var(--text-quaternary)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-brand-500";
const inputStyle: React.CSSProperties = {
  backgroundColor: "var(--bg)",
  borderColor: "var(--border)",
  color: "var(--text)",
};
